// Anonymous, non-blocking event tracking via OpenPanel.
//
// Two hard rules shape this module:
//   1. Production only. Events fire solely on peekababy.app so preview deploys,
//      tunnels, staging and a locally-served build stay silent even though
//      .env carries a real client id.
//   2. No PII, ever. The viewer URL is `${origin}${pathname}#<hostPeerId>`, so
//      we never send peer ids, connection ids or the URL hash — only roles,
//      counts, coarse states and error names.
//
// Tracking is fire-and-forget: OpenPanel's track() already queues and sends
// asynchronously, and every call here is additionally wrapped so it can never
// throw or delay the WebRTC path.
import { OpenPanel } from '@openpanel/web';

const PROD_HOSTS = new Set(['peekababy.app', 'www.peekababy.app']);

// Parcel inlines this from .env at build time. Only the *public* client id is
// ever referenced, so OPENPANEL_SECRET can never end up in the bundle.
const CLIENT_ID = process.env.OPENPANEL_CLIENT_ID;

const enabled =
  typeof window !== 'undefined' &&
  !!CLIENT_ID &&
  PROD_HOSTS.has(window.location.hostname);

// Drop anything from the first '#' onward so a peer id in the URL hash can
// never ride along on a reserved property value.
function stripHash(value) {
  return typeof value === 'string' ? value.split('#')[0] : value;
}

let op = null;
if (enabled) {
  try {
    op = new OpenPanel({
      clientId: CLIENT_ID,
      // Explicit events only. No auto screen views/links/attributes — this also
      // keeps the SDK's reserved `__path` empty so the hash never leaks.
      trackScreenViews: false,
      trackOutgoingLinks: false,
      trackAttributes: false,
      // Defense in depth: scrub the URL hash out of the reserved path/referrer
      // props on every outgoing event. Always returns true — host gating above,
      // not this filter, decides whether we track at all.
      filter: (payload) => {
        const props = payload && payload.payload && payload.payload.properties;
        if (props) {
          if ('__path' in props) props.__path = stripHash(props.__path);
          if ('__referrer' in props) props.__referrer = stripHash(props.__referrer);
        }
        return true;
      },
    });
    // The SDK seeds __referrer from document.referrer on init; blank it so we
    // never report where the viewer came from.
    op.setGlobalProperties({ __referrer: '' });
  } catch (e) {
    op = null;
  }
}

// Stable event names, referenced everywhere so dashboards stay consistent.
export const EVENTS = {
  APP_OPENED: 'app_opened',
  ROLE_SELECTED: 'role_selected',
  QR_SCANNED: 'qr_scanned',
  LINK_COPIED: 'link_copied',
  VIDEO_DIMMED: 'video_dimmed',
  MELODY_TOGGLED: 'melody_toggled',
  PEER_ERROR: 'peer_error',
  PWA_INSTALL_PROMPT_SHOWN: 'pwa_install_prompt_shown',
  PWA_INSTALLED: 'pwa_installed',
  APP_UPDATE_AVAILABLE: 'app_update_available',
  APP_UPDATE_APPLIED: 'app_update_applied',
  CAMERA_STARTED: 'camera_started',
  CAMERA_ERROR: 'camera_error',
  CAMERA_RECOVERED: 'camera_recovered',
  CAMERA_SWITCHED: 'camera_switched',
  VIEWER_CONNECTED: 'viewer_connected',
  VIEWER_DISCONNECTED: 'viewer_disconnected',
  STREAM_ESTABLISHED: 'stream_established',
  CONNECTION_LOST: 'connection_lost',
  MIC_ERROR: 'mic_error',
  TALKBACK_USED: 'talkback_used',
  VIDEO_FIT_CHANGED: 'video_fit_changed',
  ICE_FALLBACK: 'ice_fallback',
  ICE_CONNECTED: 'ice_connected',
  ICE_RESTART: 'ice_restart',
  TRUSTED_PARENT_ADDED: 'trusted_parent_added',
  TRUSTED_PARENT_REMOVED: 'trusted_parent_removed',
  BATTERY_LOW: 'battery_low',
  OUTAGE_ALARM: 'outage_alarm',
  CAMERA_FROZEN: 'camera_frozen',
  CAMERA_INTERRUPTED: 'camera_interrupted',
};

// Fire-and-forget. Never throws, never blocks the caller, no-ops off prod.
export function track(name, properties) {
  if (!op) return;
  try {
    const result = op.track(name, properties);
    if (result && typeof result.catch === 'function') result.catch(() => {});
  } catch (e) {
    /* analytics must never break the app */
  }
}

// Peer errors are dominated by *non-fatal* conditions — chiefly a parent's
// auto-reconnect loop dialing a monitor that is momentarily offline, which
// makes PeerJS emit a `peer-unavailable` error on every single retry. Tracking
// each one buries real problems under thousands of "still waiting for the
// monitor" events. Instead we record only the *rising edge* of an ongoing
// non-fatal condition (one per outage, per type); notePeerRecovery() re-arms it
// once a connection is (re)established so a genuinely new outage is captured.
// Fatal errors are rare and user-blocking, so those are always tracked.
let lastNonFatalPeerErrorType = null;

export function trackPeerError(type, fatal) {
  if (fatal) {
    track(EVENTS.PEER_ERROR, { type, fatal: true });
    return;
  }
  if (type === lastNonFatalPeerErrorType) return; // same ongoing condition — already reported
  lastNonFatalPeerErrorType = type;
  track(EVENTS.PEER_ERROR, { type, fatal: false });
}

// Called when a connection succeeds, so the next non-fatal drop is treated as a
// fresh outage rather than being suppressed as a continuation of the last one.
export function notePeerRecovery() {
  lastNonFatalPeerErrorType = null;
}
